import { useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { MobileDrawer, MobileDrawerLanguageSelector } from '@/layout/Header/MobileDrawer/MobileDrawer';
import { MyMenuDropdown } from '@/layout/Header/MyMenuDropdown';
import { LogInButton } from '@/layout/Header/UserManagementButton';
import { useUser } from '@/lib/auth';
import { GnbLanguageSelector } from '@/shared/components/LanguageSelector';
import { HomeLogo } from '../HomeLogo';
import { CreateButton } from './CreateButton';
import { type GnbButtonType } from './GnbButtonType';
import { HeaderContainer } from './HeaderContainer';
import { HeaderPlanStatusButton } from './HeaderPlanStatusButton';
import { EnterCodeButton } from './PageNavigatorButton';
const CHECKOUT_HEADER_PATH_PREFIX = '/checkout';
const PUBLIC_PATH_PREFIX = '/public';
const getProductHeaderSource = (pathname: string) => pathname.startsWith(PUBLIC_PATH_PREFIX) ? 'public_header' : 'product_header';
export function ProductHeader() {
  const {
    t
  } = useTranslation(['common']);
  const router = useRouter();
  const {
    user,
    isInitialized
  } = useUser();
  const isLoggedIn = isInitialized ? user : null;
  const isFreeUser = Boolean(isLoggedIn && isLoggedIn.planType === 'FREE');
  const [isMobileProfileMenuOpen, setIsMobileProfileMenuOpen] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const isCheckoutPage = router.pathname.startsWith(CHECKOUT_HEADER_PATH_PREFIX);
  const mobileDrawerButtons: GnbButtonType[] = [{
    label: t('common.header.pricingGuide'),
    href: '/pricing',
    target: '_blank',
    clickEventname: 'gnb_pricing_click'
  }];
  const mobileDrawerFooter = <>
      <CreateButton compact hideRightSlot className="h-[40px] min-h-[40px] w-full rounded-sm px-md text-body-md-semibold" />
      <EnterCodeButton type="normal" compact className="h-[40px] min-h-[40px] w-full rounded-sm px-md text-body-md-semibold" textClassName="truncate" />
    </>;
  if (isCheckoutPage) {
    return <HeaderContainer variant="compact" className="h-[50px] min-h-[50px] justify-start border-b border-alternative py-[12px] tablet-sm:h-[58px] tablet-sm:min-h-[58px] tablet-sm:border-b-0">
        <div className="flex shrink-0 items-center gap-3xs">
          <HomeLogo location="header" redirectUrl="/public" showFullLogoFromTabletSm className="!h-[28px] !min-h-[28px] !w-[28px] !min-w-[28px] tablet-sm:!h-[28px] tablet-sm:!min-h-[28px] tablet-sm:!w-[128.8px] tablet-sm:!min-w-[128.8px]" />
        </div>
        <div className="ml-auto flex shrink-0 items-center">
          {isInitialized && user && <>
              <MyMenuDropdown className="flex tablet-sm:hidden" triggerSize={36} hideBadge mobileMenu showAdminMenu onOpenChange={setIsMobileProfileMenuOpen} />
              <MyMenuDropdown className="hidden tablet-sm:flex" triggerSize={32} showAdminMenu />
            </>}
        </div>
      </HeaderContainer>;
  }
  return <HeaderContainer variant="compact" className="h-[50px] min-h-[50px] justify-start border-b border-alternative py-[12px] tablet-sm:h-[58px] tablet-sm:min-h-[58px] tablet-sm:border-b-0" data-sentry-element="HeaderContainer" data-sentry-component="ProductHeader" data-sentry-source-file="ProductHeader.tsx">
      <div className="flex shrink-0 items-center gap-3xs">
        <HomeLogo location="header" redirectUrl="/public" showFullLogoFromTabletSm className="!h-[28px] !min-h-[28px] !w-[28px] !min-w-[28px] tablet-sm:!h-[28px] tablet-sm:!min-h-[28px] tablet-sm:!w-[128.8px] tablet-sm:!min-w-[128.8px]" data-sentry-element="HomeLogo" data-sentry-source-file="ProductHeader.tsx" />
      </div>

      <div className="ml-[12px] flex min-w-0 flex-1 items-center justify-end gap-2xs tablet-sm:ml-auto tablet-sm:flex-none tablet-sm:shrink-0">
        <div className="flex min-w-0 flex-1 items-center justify-end gap-xs tablet-sm:hidden">
          {isLoggedIn ? <>
              {!isMobileProfileMenuOpen && <>
                  <EnterCodeButton type="normal" compact className="min-w-[30px] max-w-full shrink overflow-hidden" textClassName="truncate" />
                  <CreateButton compact hideRightSlot className="max-w-[92px] shrink-0" />
                </>}
              <MyMenuDropdown className="flex tablet-sm:hidden" triggerSize={36} hideBadge mobileMenu showAdminMenu onOpenChange={setIsMobileProfileMenuOpen} />
            </> : isInitialized && !user && <>
                {!isMobileDrawerOpen && <>
                    <CreateButton compact hideRightSlot />
                    <LogInButton type="normal" trackingLocation={getProductHeaderSource(router.pathname)} className="h-[30px] min-h-[30px] rounded-xs px-xs text-caption-lg-semibold" />
                  </>}
                <MobileDrawer buttons={mobileDrawerButtons} bodyBottomAccessory={<MobileDrawerLanguageSelector />} footerBottomAccessory={mobileDrawerFooter} onOpenChange={setIsMobileDrawerOpen} />
              </>}
        </div>
        <div className="hidden gap-xs tablet-sm:flex tablet-sm:items-center">
          {isLoggedIn && <HeaderPlanStatusButton user={isLoggedIn} location={getProductHeaderSource(router.pathname)} />}
          <EnterCodeButton type="normal" size="xs" data-sentry-element="EnterCodeButton" data-sentry-source-file="ProductHeader.tsx" />
          <CreateButton size="xs" hideRightSlot={isFreeUser} data-sentry-element="CreateButton" data-sentry-source-file="ProductHeader.tsx" />
          {isInitialized && user && <MyMenuDropdown className="hidden tablet-sm:flex" triggerSize={isFreeUser ? 36 : 32} showAdminMenu />}
          {isInitialized && !user && <>
              <LogInButton type="normal" size="xs" trackingLocation={getProductHeaderSource(router.pathname)} className="hidden tablet-sm:flex" />
              <GnbLanguageSelector size="xs" iconSize="xs" />
            </>}
        </div>
      </div>
    </HeaderContainer>;
}